import { useEffect, useRef, useState } from "react";

import {
  generatePitch,
  markPitchContacted,
  openPitch,
  savePitchDraft,
  type PitchQuota,
  type PitchState,
} from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { PLAN_REQUIRED_MESSAGE, formatDay, formatLocation, friendlyError, isPlanRequired } from "./discoverCopy";

type Props = {
  artistId: string;
  /** Venue to pitch; null closes the sheet. */
  globalVenueId: string | null;
  venueName?: string | null;
  onClose: () => void;
  onStateChange: (globalVenueId: string, state: "prospect" | "contacted") => void;
};

type ErrorLike = {
  status?: number;
  payload?: { error?: string; detail?: string | { error?: string; message?: string } };
};

/** API error code: the global handler returns {error, detail}; tolerate nested detail too. */
function errorCode(err: unknown): string | null {
  const payload = (err as ErrorLike | null)?.payload;
  if (!payload) return null;
  if (typeof payload.detail === "object" && payload.detail?.error) return payload.detail.error;
  return payload.error ?? null;
}

function generateErrorMessage(err: unknown): string {
  if (isPlanRequired(err)) return PLAN_REQUIRED_MESSAGE;
  switch (errorCode(err)) {
    case "quota_exhausted":
      return "You've used all your AI pitches for this month. You can still edit and copy this draft.";
    case "ai_disabled":
    case "not_configured":
      return "AI writing help isn't available right now. You can still write and copy your pitch.";
    case "generation_failed":
      return "We couldn't write a draft right now. You weren't charged — please try again.";
    default:
      if ((err as ErrorLike | null)?.status === 429) {
        return "You're generating pitches too quickly. Please wait a moment.";
      }
      return friendlyError(err, "We couldn't write a draft right now. Please try again.");
  }
}

export function buildCopyText(subject: string, body: string): string {
  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  return trimmedSubject ? `Subject: ${trimmedSubject}\n\n${trimmedBody}` : trimmedBody;
}

export default function PitchSheet({ artistId, globalVenueId, venueName, onClose, onStateChange }: Props) {
  const [pitch, setPitch] = useState<PitchState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState("");
  const [requestedDates, setRequestedDates] = useState("");
  const [quota, setQuota] = useState<PitchQuota | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [markingContacted, setMarkingContacted] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const copiedTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!globalVenueId) return;
    let cancelled = false;
    setPitch(null);
    setLoading(true);
    setLoadError(null);
    setGenerateError(null);
    setActionError(null);
    setSaveMessage(null);
    setCopied(false);
    setDirty(false);
    openPitch(globalVenueId, artistId)
      .then((state) => {
        if (cancelled) return;
        setPitch(state);
        setQuota(state.quota);
        setSubject(state.draft?.subject ?? "");
        setBody(state.draft?.body ?? "");
        setRecipient(state.draft?.recipient ?? state.contact.email ?? "");
        setRequestedDates(state.draft?.requested_dates ?? "");
        onStateChange(globalVenueId, state.state);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(friendlyError(err, "We couldn't open this pitch. Please try again."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalVenueId, artistId]);

  useEffect(
    () => () => {
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  const aiAvailable = pitch?.ai.available ?? false;
  const remaining = quota?.remaining ?? 0;
  const hasDraft = Boolean(subject.trim() || body.trim());
  const canGenerate = Boolean(pitch) && aiAvailable && remaining > 0 && !generating;

  const handleGenerate = async () => {
    if (!globalVenueId || !canGenerate) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      const result = await generatePitch(
        globalVenueId,
        { requested_dates: requestedDates.trim() || null, recipient: recipient.trim() || null },
        artistId,
      );
      setSubject(result.draft.subject);
      setBody(result.draft.body);
      setQuota(result.quota);
      setDirty(false);
    } catch (err) {
      setGenerateError(generateErrorMessage(err));
      if (errorCode(err) === "quota_exhausted" && quota) {
        setQuota({ ...quota, remaining: 0, used: quota.limit });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    const text = buildCopyText(subject, body);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError("Copy didn't work in this browser. Select the text and copy it manually.");
    }
  };

  const handleSave = async () => {
    if (!globalVenueId) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await savePitchDraft(globalVenueId, { subject, body, recipient: recipient.trim() || null }, artistId);
      setDirty(false);
      setSaveMessage("Draft saved");
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't save your draft. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkContacted = async () => {
    if (!globalVenueId) return;
    setMarkingContacted(true);
    setActionError(null);
    try {
      if (dirty && hasDraft) {
        await savePitchDraft(globalVenueId, { subject, body, recipient: recipient.trim() || null }, artistId);
        setDirty(false);
      }
      const state = await markPitchContacted(globalVenueId, artistId);
      setPitch(state);
      onStateChange(globalVenueId, state.state);
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't update this venue. Please try again."));
    } finally {
      setMarkingContacted(false);
    }
  };

  const venue = pitch?.venue;
  const contacted = pitch?.state === "contacted";

  return (
    <Sheet open={Boolean(globalVenueId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Pitch {venue?.name ?? venueName ?? "venue"}</SheetTitle>
          <SheetDescription>
            {venue
              ? `${formatLocation([venue.city, venue.region]) || "Location unknown"}${
                  venue.capacity ? ` · ${venue.capacity.toLocaleString()} capacity` : ""
                }`
              : "Prepare your outreach."}
          </SheetDescription>
        </SheetHeader>

        {loading && <p className="mt-4 text-sm text-muted-foreground">Opening pitch…</p>}
        {loadError && (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {loadError}
          </p>
        )}

        {pitch && (
          <div className="mt-4 space-y-4">
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                contacted ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted/50 text-muted-foreground"
              }`}
              data-testid="pitch-state"
            >
              {contacted
                ? `Contacted${pitch.contacted_at ? ` on ${formatDay(pitch.contacted_at)}` : ""}`
                : "Not contacted yet. Send your pitch however you like, then mark it contacted."}
            </div>

            <label className="block text-xs text-muted-foreground">
              Send to (optional)
              <Input
                className="mt-1"
                placeholder="Booking email, contact form, Instagram…"
                value={recipient}
                onChange={(event) => {
                  setRecipient(event.target.value);
                  setDirty(true);
                }}
                aria-label="Recipient"
              />
            </label>

            <label className="block text-xs text-muted-foreground">
              Dates you're asking about (optional)
              <Input
                className="mt-1"
                placeholder="e.g. April 2027"
                value={requestedDates}
                onChange={(event) => setRequestedDates(event.target.value)}
                aria-label="Requested dates"
              />
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => void handleGenerate()} disabled={!canGenerate}>
                {generating ? "Writing…" : hasDraft ? "Regenerate" : "Generate pitch"}
              </Button>
              <span className="text-xs text-muted-foreground" data-testid="pitch-quota">
                {!aiAvailable
                  ? "AI writing help isn't available right now"
                  : quota
                    ? remaining > 0
                      ? `${remaining} AI pitch${remaining === 1 ? "" : "es"} left this month`
                      : "No AI pitches left this month"
                    : null}
              </span>
            </div>
            {generateError && (
              <p role="alert" className="text-sm text-destructive">
                {generateError}
              </p>
            )}

            <label className="block text-xs text-muted-foreground">
              Subject
              <Input
                className="mt-1"
                value={subject}
                onChange={(event) => {
                  setSubject(event.target.value);
                  setDirty(true);
                }}
                aria-label="Subject"
              />
            </label>
            <label className="block text-xs text-muted-foreground">
              Pitch
              <Textarea
                className="mt-1 min-h-[220px]"
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  setDirty(true);
                }}
                placeholder="Generate a draft or write your own."
                aria-label="Pitch body"
              />
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => void handleCopy()} disabled={!hasDraft}>
                {copied ? "Copied" : "Copy pitch"}
              </Button>
              <Button variant="outline" onClick={() => void handleSave()} disabled={!hasDraft || saving || !dirty}>
                {saving ? "Saving…" : "Save draft"}
              </Button>
              {saveMessage && !dirty && <span className="text-xs text-muted-foreground">{saveMessage}</span>}
            </div>

            <div className="border-t border-border pt-4">
              {contacted ? (
                <p className="text-sm text-muted-foreground">This venue is in your Booking pipeline as contacted.</p>
              ) : (
                <>
                  <Button onClick={() => void handleMarkContacted()} disabled={markingContacted}>
                    {markingContacted ? "Updating…" : "Mark contacted"}
                  </Button>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Use this after you've actually sent your pitch. It moves the venue to Outreach in Booking.
                  </p>
                </>
              )}
            </div>

            {actionError && (
              <p role="alert" className="text-sm text-destructive">
                {actionError}
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
